import i18n from '@/utils/i18n';
import MapView from 'react-native-maps';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Dimensions, StyleSheet, View } from 'react-native';
import { BaseMapView } from '@usepepper/ui/molecules/map/base-map-view';
import { Business } from '@/components/locations/types';
import { Coordinate, Region } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import {
  DEBUG_SAFE_ZONE,
  INITIAL_REGION_DELTA,
  MAP_SAFE_ZONE_BOTTOM_MARGIN,
  MAP_SAFE_ZONE_SIDE_MARGIN,
  MAP_SAFE_ZONE_TOP_MARGIN,
} from '@usepepper/ui/molecules/map/constants';
import { DebugOverlay } from '@usepepper/ui/molecules/map/debug-overlay';
import { MapMarker } from '@usepepper/ui/molecules/map/map-marker';
import { PillButton } from '@/components/pill-button';
import { shadow } from '@/styles';
import { UserLocationMarker } from '@usepepper/ui/molecules/map/user-location-marker';

interface Props {
  collapsedSheetSizePercent: number;
  directHit: boolean;
  dragLeadGeneratorLocation: Coordinate | null;
  leadGeneratorLocation: Coordinate;
  mapChanged: boolean;
  nearbyBusinesses?: Array<Business>;
  onBusinessSelect: (selectedBusiness: Business) => void;
  onMapTouch: () => void;
  onPanDrag: () => void;
  onRegionChangeComplete: (e: Region) => void;
  onSearchArea: () => void;
  selectedBusiness?: Business;
  userLocation: Coordinate | null;
}

export const LeadGeneratorMap: React.FC<Props> = ({
  collapsedSheetSizePercent,
  directHit,
  dragLeadGeneratorLocation,
  leadGeneratorLocation,
  mapChanged,
  nearbyBusinesses,
  onBusinessSelect,
  onMapTouch,
  onPanDrag,
  onRegionChangeComplete,
  onSearchArea,
  selectedBusiness,
  userLocation,
}) => {
  const [styles, theme] = useStyles();
  const mapRef = useRef<MapView>(null);
  const { height, width } = Dimensions.get('window');

  // State to track map animation, e.g. panning the map when results change
  const [animateToRegion, setAnimateToRegion] = useState<Region | undefined>(
    undefined,
  );
  const [currentRegion, setCurrentRegion] = useState<Region | null>(null);

  const searchButtonAnimation = useRef(new Animated.Value(0)).current;
  const [searchButtonOpacity, setSearchButtonOpacity] = useState(0);

  useEffect(() => {
    const listenerId = searchButtonAnimation.addListener(({ value }) => {
      setSearchButtonOpacity(value);
    });

    if (dragLeadGeneratorLocation || mapChanged) {
      Animated.timing(searchButtonAnimation, {
        duration: 200,
        toValue: 1,
        useNativeDriver: false,
      }).start(() => searchButtonAnimation.removeListener(listenerId));
    } else {
      Animated.timing(searchButtonAnimation, {
        duration: 200,
        toValue: 0,
        useNativeDriver: false,
      }).start(() => searchButtonAnimation.removeListener(listenerId));
    }
  }, [dragLeadGeneratorLocation, mapChanged, searchButtonAnimation]);

  /**
   * Once the nearby business data has loaded, calculate the furthest away
   * business to determine what zoom we should animate our map to.
   */
  useEffect(() => {
    if (nearbyBusinesses) {
      const { latitudeDelta, longitudeDelta } = nearbyBusinesses.reduce(
        (acc, business) => {
          const latDelta = Math.abs(
            Math.abs(business.latitude) -
              Math.abs(leadGeneratorLocation.latitude),
          );
          const longDelta = Math.abs(
            Math.abs(business.longitude) -
              Math.abs(leadGeneratorLocation.longitude),
          );

          return {
            latitudeDelta:
              latDelta > acc.latitudeDelta ? latDelta : acc.latitudeDelta,
            longitudeDelta:
              longDelta > acc.longitudeDelta ? longDelta : acc.longitudeDelta,
          };
        },
        {
          latitudeDelta: 0,
          longitudeDelta: 0,
        },
      );

      // Scaling this based on what amount of the map is visible, e.g.
      // e.g. if there's only 25% of the map in view, fit everything in that
      const scaledLatDelta =
        latitudeDelta / (1 - collapsedSheetSizePercent / 100);

      // Set the region to which we want the map to animate
      // The base component will react to the change and move the map
      setAnimateToRegion({
        latitude:
          leadGeneratorLocation.latitude -
          scaledLatDelta * (collapsedSheetSizePercent / 100),
        latitudeDelta: scaledLatDelta,
        longitude: leadGeneratorLocation.longitude,
        longitudeDelta,
      });
    }
  }, [collapsedSheetSizePercent, leadGeneratorLocation, nearbyBusinesses]);

  const handlePinPress = async (
    business: Business,
    point: { x: number; y: number },
  ) => {
    if (!currentRegion || !mapRef.current) {
      return;
    }
    const { x, y } = point;

    // calculate distances to each edge of the screen
    const distanceTop = y;
    const distanceBottom = height - y;
    const distanceLeft = x;
    const distanceRight = width - x;

    // Check if within your "safe zone":
    const isInsideCenterZone =
      distanceTop > MAP_SAFE_ZONE_TOP_MARGIN &&
      distanceBottom > MAP_SAFE_ZONE_BOTTOM_MARGIN &&
      distanceLeft > MAP_SAFE_ZONE_SIDE_MARGIN &&
      distanceRight > MAP_SAFE_ZONE_SIDE_MARGIN;

    // If outside center zone, re-center the map around this coordinate
    if (!isInsideCenterZone) {
      mapRef.current.animateToRegion({
        latitude: business.latitude,
        latitudeDelta: currentRegion.latitudeDelta,
        longitude: business.longitude,
        longitudeDelta: currentRegion.longitudeDelta,
      });
    }

    // In either case, display the business info card
    onBusinessSelect(business);
  };

  const scaledInitialLatDelta =
    INITIAL_REGION_DELTA / (1 - collapsedSheetSizePercent / 100);

  // only show single marker if directHit is true and we haven't panned
  const showSingleMarker = directHit && selectedBusiness && !mapChanged;

  return (
    <View style={StyleSheet.absoluteFill}>
      <BaseMapView
        animateToRegion={animateToRegion}
        initialRegion={{
          latitude:
            leadGeneratorLocation.latitude -
            scaledInitialLatDelta * (collapsedSheetSizePercent / 100),
          latitudeDelta: scaledInitialLatDelta,
          longitude: leadGeneratorLocation.longitude,
          longitudeDelta: INITIAL_REGION_DELTA,
        }}
        interactive
        mapRef={mapRef}
        onPanDrag={onPanDrag}
        onPress={onMapTouch}
        onRegionChangeComplete={(region) => {
          setCurrentRegion(region);
          onRegionChangeComplete(region);
        }}
      >
        {showSingleMarker ? (
          <MapMarker
            accessibilityLabel="map-marker-selected"
            coordinate={{
              latitude: selectedBusiness!.latitude,
              longitude: selectedBusiness!.longitude,
            }}
            fill={
              selectedBusiness!.restaurant_uuid
                ? theme.colors.primary
                : theme.colors.accent
            }
            onPress={() => onBusinessSelect(selectedBusiness!)}
            selected
          />
        ) : (
          nearbyBusinesses?.map((business, index) => (
            <MapMarker
              accessibilityLabel={`map-marker-${index}`}
              coordinate={{
                latitude: business.latitude,
                longitude: business.longitude,
              }}
              fill={
                business.restaurant_uuid
                  ? theme.colors.primary
                  : theme.colors.accent
              }
              key={business.yelp_business_id}
              onPress={async () => {
                if (!mapRef.current) {
                  return;
                }

                const { x, y } = await mapRef.current.pointForCoordinate({
                  latitude: business.latitude,
                  longitude: business.longitude,
                });

                await handlePinPress(business, { x, y });
              }}
              selected={
                selectedBusiness?.yelp_business_id === business.yelp_business_id
              }
            />
          ))
        )}
        {userLocation ? (
          <UserLocationMarker
            coordinate={{
              latitude: userLocation.latitude,
              longitude: userLocation.longitude,
            }}
          />
        ) : null}
      </BaseMapView>
      {mapChanged ? (
        <View style={styles.searchPillContainer}>
          <Animated.View
            pointerEvents={dragLeadGeneratorLocation ? 'auto' : 'none'}
            style={[
              {
                opacity: searchButtonOpacity,
              },
              styles.searchPillWrapper,
            ]}
          >
            <PillButton
              button={{
                active: false,
                collapsed: false,
                label: i18n.t('search_this_area'),
                onClear: () => {},
                onPress: onSearchArea,
              }}
              style={styles.searchPill}
            />
          </Animated.View>
        </View>
      ) : null}
      {DEBUG_SAFE_ZONE && <DebugOverlay />}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  searchPill: {
    ...shadow({
      elevation: 5,
      opacity: 0.1,
      radius: 24,
      x: 0,
      y: 12,
    }),
    backgroundColor: theme.colors.white,
  },
  searchPillContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  searchPillWrapper: {
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    flexDirection: 'column',
    paddingTop: 16,
  },
}));
